import type {ComponentPropsWithoutRef} from 'react';
import {forwardRef} from 'react';
import {Slot} from '@radix-ui/react-slot';
import type {VariantProps} from 'class-variance-authority';
import {cva} from 'class-variance-authority';

import {cn} from '../../shared/tailwind';

const button = cva(
	'no-underline inline-flex items-center justify-center gap-2 disabled:opacity-50 rounded-sm transition-all cursor-pointer font-semibold whitespace-nowrap',
	{
		variants: {
			variant: {
				primary: '',
				secondary: '',
				tertiary: '',
			},
			severity: {
				default: '',
				danger: '',
			},
			size: {
				// NOTE: The height and padding is set lower than the design to adjust for the base font being lighter than Roboto used in the design (which feels like a bug since other elements use Rajdhani).
				sm: 'text-sm lg:text-base h-8 lg:h-10 px-2 lg:px-3 border',
				md: 'text-base lg:text-lg h-10 lg:h-12 px-3 lg:px-4 border-2',
				lg: 'text-lg lg:text-xl h-12 lg:h-14 px-4 lg:px-5 border-2',
			},
		},
		compoundVariants: [
			// === PRIMARY variant ===
			{
				variant: 'primary',
				severity: 'default',
				className: [
					'bg-highlight-dm',
					'text-neutral-100',
					'not-disabled:hover:bg-[#00374B]',
					'border-highlight-dm',
					'not-disabled:hover:border-[#00374B]',
				],
			},
			{
				variant: 'primary',
				severity: 'danger',
				className: [
					'bg-red-800',
					'text-white',
					'not-disabled:hover:bg-red-700',
					'border-red-800',
					'not-disabled:hover:border-red-700',
				],
			},

			// === SECONDARY variant ===
			{
				variant: 'secondary',
				severity: 'default',
				className: [
					'border-primary',
					'text-primary',
					'not-disabled:hover:text-secondary-dm',
					'not-disabled:hover:border-secondary-dm',
					'not-disabled:hover:bg-secondary-dm/10',
				],
			},
			{
				variant: 'secondary',
				severity: 'danger',
				className: [
					'border-red-400',
					'text-red-400',
					'not-disabled:hover:text-red-300',
					'not-disabled:hover:border-red-300',
					'not-disabled:hover:bg-red-500/10',
				],
			},

			// === TERTIARY variant ===
			{
				variant: 'tertiary',
				severity: 'default',
				className: [
					'text-primary',
					'border-transparent',
					'underline',
					'underline-offset-5',
					'not-disabled:hover:text-secondary-dm',
					'not-disabled:hover:bg-secondary-dm/10',
					'not-disabled:hover:no-underline',
				],
			},
			{
				variant: 'tertiary',
				severity: 'danger',
				className: [
					'text-red-400',
					'not-disabled:hover:text-red-300',
					'not-disabled:hover:bg-red-500/10',
					'underline',
					'border-transparent',
					'underline-offset-4',
					'not-disabled:hover:no-underline',
				],
			},
		],
		defaultVariants: {
			variant: 'primary',
			severity: 'default',
		},
	},
);

export type ButtonProps = VariantProps<typeof button> &
	ComponentPropsWithoutRef<'button'> & {
		/**
		 * Change the default rendered element for the one passed as a child, merging their props and behavior.
		 * @default false
		 */
		asChild?: boolean;
	};

/**
 * Button used for interaction
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			children,
			variant = 'primary',
			severity = 'default',
			size = 'md',
			type,
			className,
			asChild,
			...rest
		},
		ref,
	) => {
		const Component = asChild ? Slot : 'button';

		return (
			<Component
				ref={ref}
				{...(type ? {type} : {})}
				className={cn(button({variant, severity, size}), className)}
				{...rest}
			>
				{children}
			</Component>
		);
	},
);

Button.displayName = 'Button';
